<script setup lang="ts">
import { router } from '@inertiajs/vue3'
import { ChevronDown } from 'lucide-vue-next'
import { computed, reactive, ref } from 'vue'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import { requestAdditionalLoanApplicationDocumentsPath } from '@/routes'

interface Props {
  applicationId: string
  optionalDocNames: string[]
  existingDocNames: string[]
}

const props = defineProps<Props>()

const availableDocNames = computed(() =>
  props.optionalDocNames.filter((name) => !props.existingDocNames.includes(name)),
)

const open = ref(false)
const selected = reactive<Record<string, boolean>>({})
const processing = ref(false)

const selectedNames = computed(() => Object.keys(selected).filter((k) => selected[k]))

function submit(e: Event) {
  e.preventDefault()
  if (selectedNames.value.length === 0 || processing.value) return
  processing.value = true

  router.post(
    requestAdditionalLoanApplicationDocumentsPath(props.applicationId),
    { names: selectedNames.value },
    {
      preserveScroll: true,
      onSuccess: () => { open.value = false },
      onFinish: () => {
        processing.value = false
        Object.keys(selected).forEach((k) => delete selected[k])
      },
    },
  )
}
</script>

<template>
  <DropdownMenu v-model:open="open">
    <DropdownMenuTrigger as-child>
      <Button size="sm" variant="outline" class="w-full justify-between">
        Request Additional Documents
        <ChevronDown class="size-3.5 opacity-50" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-72" align="end">
      <DropdownMenuLabel class="text-xs font-medium">Select documents to request</DropdownMenuLabel>
      <DropdownMenuSeparator />
      <div class="max-h-56 overflow-y-auto">
        <p v-if="availableDocNames.length === 0" class="text-muted-foreground px-2 py-3 text-xs">
          All optional documents already requested.
        </p>
        <div
          v-for="name in availableDocNames"
          :key="name"
          class="hover:bg-accent flex cursor-pointer items-start gap-2 rounded-sm px-2 py-1.5"
          @click.self="selected[name] = !selected[name]"
        >
          <Checkbox
            :id="`rad-${name}`"
            :checked="selected[name] ?? false"
            class="mt-0.5 shrink-0"
            v-model="selected[name]"
          />
          <Label :for="`rad-${name}`" class="cursor-pointer text-sm leading-snug font-normal">
            {{ name }}
          </Label>
        </div>
      </div>
      <DropdownMenuSeparator />
      <div class="p-1">
        <Button size="sm" class="w-full" :disabled="selectedNames.length === 0 || processing" @click="submit">
          Request
          <template v-if="selectedNames.length > 0">{{ selectedNames.length }}</template>
          Document{{ selectedNames.length !== 1 ? 's' : '' }}
        </Button>
      </div>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
